package com.shadowings.gero.domain.model

import kotlinx.serialization.Serializable

@Serializable
data class UserData(
    val stars: Map<String, Int> = mapOf(),
    val perfect: Set<String> = setOf(),
    val explanations: Set<String> = setOf(),
    @kotlinx.serialization.SerialName("coins")
    val experience: Int = 0,
    val strike: Int = 0,
    val tutorialDone: Boolean? = null,
    val alynColors: Map<String, Int> = mapOf(),
    val niloColors: Map<String, Int> = mapOf(),
    val zetaColors: Map<String, Int> = mapOf(),
    val maskoColors: Map<String, Int> = mapOf(),
    val chapter: Int = 1,
    val hearts: Int = 10, // default gestito da GameConstants.DEFAULT_HEARTS nel layer data
    val lastOpenDate: Int = 0,
    val lastQuizDate: Int = 0,
    val favoriteHieros: Set<String> = setOf(),
    val favoriteWords: Set<String> = setOf(),
    val language: String = "",
    // String, not Long: kotlin.Long compiles to a two-int JS object, which Firestore's JS SDK
    // rejects outright when writing a document ("Unsupported field value: a custom Long
    // object"). GitLive's Firebase encoder/decoder freely coerces String<->Number on decode in
    // both directions, so this stays compatible with already-stored numeric Firestore documents
    // and with old app versions still reading this field as a Long.
    val consumablesUpdatedAt: String = "0",
    val walkthroughDone: Boolean = false,
    val compendioWalkthroughDone: Boolean = false,
    val builderWalkthroughDone: Boolean = false,
    val charactersWalkthroughDone: Boolean = false,
    val profileWalkthroughDone: Boolean = false,
    val completedQuizDates: List<String> = emptyList(),
    val lastModified: String = "0", // String for the same Kotlin/JS Long reason as consumablesUpdatedAt above
    val lastSeenChangelogVersion: String = "",
    val savedBuilderWords: List<SavedBuilderWord> = emptyList(),
    val reviewPromptDone: Boolean = false,
    val isFullVersionUnlocked: Boolean = false,
    // Key = level (as String, for Firestore map-key compatibility) that granted a Streak
    // Restoration token, value = whether it has been redeemed. Deriving "how many are available"
    // from this dictionary (count of `false` values) instead of a plain counter makes cloud-sync
    // merges idempotent: unioning keys never double-grants the same level's token across two
    // devices, and "used" (true) always wins over "unused" (false) per key. See
    // CloudSyncRepositoryImpl.mergeStreakRestorationTokens.
    val streakRestorationTokens: Map<String, Boolean> = emptyMap(),
    val streakRestorationBackfillDone: Boolean = false,
    // True once the user has tapped the streak stat while a badge was showing. Reset to false by
    // UserDataRepositoryImpl.ensureStreakRestorationTokensUpTo whenever a new token is granted, so
    // the badge reappears on the next level-up instead of staying dismissed forever.
    val streakRestorationBadgeSeen: Boolean = false,
    // Key = a stable poll-content identifier (see FeaturePollOption.POLL_KEY), value = whether
    // the user has submitted an answer for that specific poll. Keyed by content instead of a
    // single flag so a future poll with different options can use a new key and resurface for
    // everyone, without a schema migration. Merged like streakRestorationTokens above (key-union,
    // "answered" always wins) — see CloudSyncRepositoryImpl.mergeAnsweredFeaturePolls.
    val answeredFeaturePolls: Map<String, Boolean> = emptyMap(),
    // A growing history of questions the user answered wrong, for a future "review your
    // mistakes" section. Unlike savedBuilderWords (a small, user-curated list), this list grows
    // passively and without a natural bound, so it is NOT merged per-entry with a union+tombstone
    // rule (that would make it — and the synced Firestore document — grow forever). Instead the
    // whole list wins wholesale from whichever side's `wrongAnswersUpdatedAt` is newer. See
    // CloudSyncRepositoryImpl.mergeUserData.
    val wrongAnswers: List<WrongAnswerRecord> = emptyList(),
    // Bumped only when `wrongAnswers` itself changes (recordWrongAnswer/removeWrongAnswer), not by
    // unrelated local edits — unlike the whole-UserData `lastModified`, which is bumped by every
    // local write (language, theme, tutorial flags, ...). Using the generic `lastModified` to gate
    // this field used to let an unrelated local edit on a freshly logged-in device (still empty of
    // wrongAnswers) look "more recently modified" than the cloud copy and silently wipe it. String
    // for the same Kotlin/JS Long reason as consumablesUpdatedAt above.
    val wrongAnswersUpdatedAt: String = "0",
    // Same idea as wrongAnswersUpdatedAt, one per favorites set: bumped only by
    // toggleFavoriteHiero/toggleFavoriteWord, so an unrelated local edit on a fresh install (e.g.
    // onboarding) can't make its still-empty favorites win the merge and wipe the cloud's copy.
    // Kept separate so favoriting a hieroglyph doesn't also make the (still empty) word set win.
    val favoriteHierosUpdatedAt: String = "0",
    val favoriteWordsUpdatedAt: String = "0",
)
